// Проверка микрофона без запуска сессии.
//
// Отвечает на вопрос, который иначе стоит секунд загрузки моделей: слышит ли
// браузер что-нибудь вообще, и какой у речи уровень. Второе важнее первого —
// именно с этим числом сравнивается порог детектора речи, и без него порог
// выставляют наугад.
//
// Меряет тот же worklet, что и настоящий захват. Не `AnalyserNode`: его данные
// зависят от того, тянет ли граф этот узел, и в headless-браузере он молчал
// даже подключённый к выходу. Worklet же проверен на всём тракте.

import { captureConstraints, trackSampleRate } from "./devices.ts";

/** Сколько измерять. Дольше держать микрофон без дела незачем. */
const DURATION_MS = 8_000;

export interface MeterReading {
  rms: number;
  peak: number;
}

export interface MeterOptions {
  deviceId: string | null;
  buildTag: string;
  /**
   * Показания для индикатора, двадцать раз в секунду. Итог замера считается
   * отдельно, по редким `capture-stats`: максимум за период надёжнее максимума
   * мгновенных значений.
   */
  onLevel: (reading: MeterReading) => void;
  /**
   * Итог замера. Вторым аргументом — настоящая частота дорожки: проверку
   * микрофона нажимают первой, и узкую полосу правильно поймать здесь, а не
   * через минуту разговора. `null` — браузер частоту не сообщил.
   */
  onDone: (best: MeterReading, sampleRate: number | null) => void;
}

/**
 * Померить уровень микрофона. Возвращает функцию досрочной остановки.
 *
 * Поток гасится в любом случае — и по истечении времени, и при остановке:
 * открытый микрофон держит индикатор записи и занимает устройство.
 */
export async function measureInput(options: MeterOptions): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia(
    captureConstraints(options.deviceId),
  );

  // Дальше микрофон уже открыт, и обещание выше — «поток гасится в любом
  // случае» — держится вот этим `try`, а не само собой. Упасть здесь значит
  // оставить устройство занятым до перезагрузки страницы: `stop` наружу ещё не
  // вернулся, и ссылки на поток нет ни у кого.
  let opened: AudioContext | null = null;
  try {
    const sampleRate = trackSampleRate(stream.getAudioTracks()[0]!);

    const context = new AudioContext();
    opened = context;
    // Контекст создаётся приостановленным, а спящий граф не обрабатывает ничего.
    await context.resume();
    await context.audioWorklet.addModule(`capture-processor.js?v=${options.buildTag}`);

    const source = context.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(context, "capture", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: { blockSize: 480 },
    });

    // Тот же нулевой гейн до выхода, что и в захвате: без пути к destination
    // граф узел не тянет, и process() не вызывается вовсе.
    const silent = context.createGain();
    silent.gain.value = 0;
    source.connect(node).connect(silent).connect(context.destination);

    const best: MeterReading = { rms: 0, peak: 0 };
    let stopped = false;

    node.port.onmessage = (event: MessageEvent<{ type: string; rms: number; peak: number }>) => {
      if (event.data.type === "level") {
        options.onLevel({ rms: event.data.rms, peak: event.data.peak });
        return;
      }
      if (event.data.type !== "capture-stats") {
        return;
      }
      best.rms = Math.max(best.rms, event.data.rms);
      best.peak = Math.max(best.peak, event.data.peak);
    };

    const stop = (): void => {
      if (stopped) {
        return;
      }
      stopped = true;
      clearTimeout(deadline);
      source.disconnect();
      node.disconnect();
      silent.disconnect();
      for (const track of stream.getTracks()) {
        track.stop();
      }
      void context.close();
      options.onDone(best, sampleRate);
    };

    const deadline = setTimeout(stop, DURATION_MS);
    return stop;
  } catch (error) {
    for (const track of stream.getTracks()) {
      track.stop();
    }
    void opened?.close();
    // Разрешение выдано, микрофон открылся — сказать «не открыть микрофон»
    // значит отправить человека разбираться туда, где всё в порядке.
    throw new MeterSetupError(String(error), { cause: error });
  }
}

/**
 * Отказ уже после того, как микрофон открылся.
 *
 * Отдельный тип нужен ровно затем, чтобы интерфейс не назвал причиной
 * разрешение: к этому моменту оно выдано, и искать там нечего.
 */
export class MeterSetupError extends Error {}
